/**
 * The two ATOMIC adapter-response envelope emitters, homed in `const/` rather than `utils/` so pure `utils/` leaves can
 * value-import them: `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans utils -> utils value edges with no allowlist, which
 * previously foreclosed the builders to every `utils/` leaf and forced hand-rolled byte-copies of their output plus
 * injected-atom seams that existed only to carry them across the floor.
 *
 * Scope is the ATOMS ONLY. The status-specialized wrappers (`buildBadRequest`, `buildNotFound`, `buildForbidden`, ...)
 * and the context-validation wrapper family stay in `utils/adapters/response-core.ts` and reach these through this
 * leaf: they have no `utils/` or `const/` consumers, so hosting them here would be exactly the relocation candidate
 * `QUAL-CONST-FUNCTION-DISCIPLINE-001`'s `fix` arm (b) names, and their directives could not be written truthfully.
 */

import { HTTP_STATUS } from '@federa/const/http-status';
import { describeGenerationFailure } from '@federa/const/error-messages';
import { ERROR_BODY_PREVIEW_MAX_CHARS } from '@federa/const/observability-error-preview';
import { asPlainObjectOrNull, isPlainObject, isRecordValue } from '@federa/const/record-guards';
import type {
	AdapterErrorResponse,
	AdapterResult,
	AdapterSuccessResponse,
	GenerationResolutionContext,
} from '@federa/types/adapters-response';

/**
 * Runtime narrower for `AdapterResult` -- the READ half of the two emitters below, homed beside them so the shape a
 * producer ASSERTS and the shape a consumer ADMITS cannot drift.
 *
 * It exists because `isRecordValue` is not enough and reads as though it were: a record check proves the value is an
 * object and nothing more, so an envelope relayed from an untyped `input[...]` slot through
 * `as AdapterResult` clears it while carrying neither `success` nor `status`. Every consumer downstream -- including a
 * declared `outputSchema` promising those two members -- then rests on a cast nothing verified.
 *
 * The test is exactly the two members BOTH arms of the union declare (`AdapterSuccessResponse` and
 * `AdapterErrorResponse`), which is the same intersection `ADAPTER_RESULT_OUTPUT_SCHEMA`
 * (`const/flow-passthrough-output-schemas.ts`) declares `required` -- so a value this guard admits is a value that
 * schema describes truthfully.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the membership test over the same `AdapterResult` union the two
 * emitters below construct.
 */
export function isAdapterResult(value: unknown): value is AdapterResult {
	const envelope = value as Partial<AdapterResult>;
	return isRecordValue(value) && typeof envelope.success === 'boolean' && typeof envelope.status === 'number';
}

/**
 * A plain-object body that states its OWN verdict: a boolean `success`, status or not -- the weaker sibling of
 * {@link isAdapterResult}, for the relays that pass a handler's own failure body through rather than re-wrapping it
 * (the browser-run legacy envelope, the wishlist create-record step). An array is refused (`isPlainObject`).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the membership test those two relays spelled inline.
 */
export function hasBooleanSuccess(value: unknown): value is Record<string, unknown> & { success: boolean } {
	return isPlainObject(value) && typeof value.success === 'boolean';
}

/**
 * WHICH LEVEL OF A RELAYED ENVELOPE THE PRODUCER'S OWN FACTS SIT ON -- the second READ member, homed beside the guard
 * and the two emitters because THIS PAIR IS WHAT CREATES THE AMBIGUITY and nothing else does.
 *
 * A relay reads its far side's body, hands it back VERBATIM when {@link isAdapterResult} admits it, and otherwise WRAPS
 * it with {@link buildAdapterSuccess}. Those two outcomes put the facts on DIFFERENT levels: `{success, status,
 * verified}` on the tagged path, `{success, status, data: { success, data: { verified } }}` on the wrapped one. A
 * consumer that pins either level reads `undefined` the moment the producer takes the other -- and `undefined` is not a
 * loud failure, it is a boolean that answers `false` forever. `voice-otp-verify` shipped exactly that: it pinned ONE
 * level against a DO body that carries no `status`, so `verified` was false on every run, the tier UPDATE never ran,
 * and the pass sentence was unreachable. BOTH levels are therefore read here, once, for every consumer.
 *
 * IT IS NOT A SUBSTITUTE FOR A ONE-HOP UNWRAP AND MUST NOT BE ADOPTED AS ONE. `readFlowSection`
 * (`components/connect/connect-ingress-guards.ts`) unwraps EXACTLY ONE level and that is its contract: its facts sit
 * at the first level, so a second hop there would narrow a `data` member of the producer's own the day one appears,
 * answer nothing, and take the breaker alarm off the screen. Two hops belong only where the producer's body may itself
 * be a whole envelope, which is the relay case above.
 *
 * ARRAYS ARE NOT FACTS: `isPlainObject` at both hops, so a `data` array is left as the value it is rather than being
 * descended into and indexed by key.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the level DECISION over the same two envelope shapes the guard and
 * the success emitter above produce, so a producer that starts tagging its body moves every consumer at once.
 */
export function readAdapterFacts(envelope: unknown): Record<string, unknown> | undefined {
	if (!isPlainObject(envelope)) return undefined;
	return envelopeDataOrSelf(envelopeDataOrSelf(envelope));
}

/**
 * ONE producer-spanning unwrap hop: the envelope's `data` OBJECT where the producer sent one, and the envelope itself
 * where it did not -- the atom {@link readAdapterFacts} takes twice, and the whole read at a consumer whose facts sit
 * on the first level (a buy preview, a replay verdict, a session mint). `isPlainObject`, so a `data` array is left as
 * the value it is rather than read as facts.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one-level decision the two-level read above composes.
 */
export function envelopeDataOrSelf(envelope: Record<string, unknown>): Record<string, unknown> {
	return asPlainObjectOrNull(envelope['data']) ?? envelope;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the canonical error-envelope emitter, homed in const/ so pure
 * utils/ leaves can value-import it without a forbidden cross-utils sibling dependency -- that layer constraint is why
 * it lives here, and it holds across the whole consumer set rather than any enumerated sample. Derives the envelope's
 * status default from the `HTTP_STATUS` SSOT; `error` mirrors `message`, so a differing user-facing label needs
 * `buildLabeledAdapterError` instead.
 *
 * WHAT A FLOW `outputSchema` OWES A GATE ARM BUILT ON THIS, hoisted here 2026-08-24 from the six market-maker leaves
 * that each carried it (QUAL-CROSS-FILE-COMMENT-PROSE-RUN-DUP-001): `error` and `status` ride the wire on every
 * `success: false` answer -- the message is mirrored into `error` and the HTTP status sits INSIDE the body -- so both
 * belong in the declared properties. They are one-arm members exactly like the success arm's `data`, and a schema that
 * declares `data` while omitting these two is describing only half of what the flow can answer.
 */
export function buildAdapterError(
	message: string,
	status: number = HTTP_STATUS.INTERNAL_SERVER_ERROR,
	data?: unknown,
): AdapterErrorResponse {
	const response: AdapterErrorResponse = {
		error: message,
		message,
		status,
		success: false,
	};
	if (data !== undefined) response.data = data;
	return response;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the CODEGEN-FAILURE error envelope -- `<prefix>: <detail>` at an
 * EXPLICIT 500, where the detail is {@link describeGenerationFailure}'s narrowing of the thrown value. A generator that
 * threw is a server-side fault by construction: nothing a caller sends can make the codegen succeed, so the status is
 * spelled here rather than inherited from {@link buildAdapterError}'s parameter default
 * (`QUAL-ERROR-BUILDER-IMPLICIT-STATUS-001` -- the wire status of a branch is a decision its author makes).
 *
 * ONE DECISION, previously spelled at FOUR generator flow leaves: the narrowing, the `<prefix>: <detail>`
 * interpolation grammar, and the `HTTP_STATUS.INTERNAL_SERVER_ERROR` status. jscpd surfaced the resulting eight-line tail as a cross-file record between
 * the flow-names and input-types generator hosts (since merged into `flows/flow-registry-artifacts-generate-host.ts`); folding only the narrowing left it
 * ABOVE `.jscpd.json`'s `minLines: 6`, so the whole tail collapses here instead. What stays at each leaf is the one
 * thing that differs: its own failure PREFIX.
 *
 * Homed beside {@link buildAdapterError} rather than with the detail helper because the PRODUCT is an envelope and this
 * leaf is the envelope SSOT; the const-to-const value import is the direction that carries no cycle
 * (`const/error-messages.ts` reaches only `@federa/const/http-status`).
 */
export function generationFailureAdapterError(prefix: string, err: unknown, context: GenerationResolutionContext): AdapterErrorResponse {
	// THE RESOLUTION CONTEXT IS REQUIRED (2026-09-08, why-the-failure-envelope-drops-the-resolved-root-because-the-
	// adapter-error-shape-has-no-slot): a generator knows at the instant it fails which root it resolved and whether it
	// was aimed there; without a slot the envelope said only what was caught, and an aimed run that fell back to the
	// desk and an unaimed run reported byte-identically. Required, not optional, so the compiler names every caller.
	return buildAdapterError(`${prefix}: ${describeGenerationFailure(err)}`, HTTP_STATUS.INTERNAL_SERVER_ERROR, context);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the canonical success-envelope emitter, homed in const/ so pure
 * utils/ leaves can value-import it without a forbidden cross-utils sibling dependency -- that layer constraint is why
 * it lives here, and it holds across the whole consumer set rather than any enumerated sample. Derives the envelope's
 * status default from the `HTTP_STATUS` SSOT. Key ORDER is part of the contract -- `success`, `status`, then `data`,
 * then `message` -- and an omitted `data`/`message` is ABSENT from the emitted object rather than present-as-undefined,
 * which is what makes a hand-rolled copy byte-reproducible and therefore invisible to every sibling envelope rule.
 */
export function buildAdapterSuccess<T = unknown>(data?: T, message?: string, status: number = HTTP_STATUS.OK): AdapterSuccessResponse<T> {
	const response: AdapterSuccessResponse<T> = { success: true, status };
	if (data !== undefined) response.data = data;
	if (message !== undefined) response.message = message;
	return response;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the four UPSTREAM-TRANSPORT failure envelopes an outbound API
 * adapter emits, spelled once. Same shape of fold as {@link generationFailureAdapterError} -- what repeats is the
 * MESSAGE GRAMMAR plus the status choice, and what stays at each adapter is its own `LABEL` and the detail it derives.
 *
 * ONE DECISION, previously spelled at BOTH Cloudflare API adapters (`tools/cf-analytics/graphql-query.ts`,
 * `tools/cf-observability/query-client.ts`), which is also why the fold could not live in `utils/`: those are `tools/`
 * leaves and `QUAL-NO-CROSS-TOOL-IMPORTS-001` bans tools -> tools with no allowlist, so a shared home there is
 * foreclosed. These are pure string assembly over {@link buildAdapterError}, so `const/` hosts them exactly as it
 * already hosts the emitter itself.
 *
 * THE `BAD_GATEWAY` IS THE DECISION, not a default: a network failure and an unparseable body are both "the UPSTREAM
 * misbehaved", so reporting either as this adapter's own 500 would blame the wrong hop.
 */
export function upstreamNetworkAdapterError(label: string, detail: string): AdapterErrorResponse {
	return buildAdapterError(`${label} network error: ${detail}`, HTTP_STATUS.BAD_GATEWAY);
}

/** The unparseable-body arm; see {@link upstreamNetworkAdapterError} for the status rationale.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): one of the four upstream-transport envelopes over
 * {@link buildAdapterError}, sharing that sibling's home rationale -- both `tools/` adapters bind it and
 * `QUAL-NO-CROSS-TOOL-IMPORTS-001` forecloses a shared `tools/` home. */
export function upstreamJsonParseAdapterError(label: string, detail: string): AdapterErrorResponse {
	return buildAdapterError(`${label} JSON parse error: ${detail}`, HTTP_STATUS.BAD_GATEWAY);
}

/**
 * The non-2xx arm, which RELAYS the upstream status rather than substituting one, because that status is the
 * diagnosis. The body PREVIEW bound lives here rather than at each adapter -- it is one decision about how much
 * upstream text rides an error envelope, and both sites had spelled the `ERROR_BODY_PREVIEW_MAX_CHARS` slice for
 * themselves. `hint` is the caller's own permission advice, appended verbatim including the empty string an adapter
 * passes when the status does not implicate the credential.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): one of the four upstream-transport envelopes over
 * {@link buildAdapterError}, and the `ERROR_BODY_PREVIEW_MAX_CHARS` bound it applies comes from the const leaf that
 * owns that decision (`const/observability-error-preview.ts`) rather than being re-chosen here; both `tools/` adapters
 * bind this arm and a shared `tools/` home is foreclosed.
 *
 * BUNDLED RATHER THAN POSITIONAL, and not only to sit inside the `max-params` cap: `bodyText` and `hint` are both
 * strings in adjacent positions, so a transposition type-checks, runs, and produces an error envelope whose upstream
 * body and permission advice have swapped places. Naming them at the call site is what makes that unspellable.
 */
export function upstreamHttpAdapterError(upstream: {
	label: string;
	status: number;
	bodyText: string;
	hint: string;
}): AdapterErrorResponse {
	const { label, status, bodyText, hint } = upstream;
	return buildAdapterError(`${label} HTTP ${status}: ${bodyText.slice(0, ERROR_BODY_PREVIEW_MAX_CHARS)}.${hint}`, status);
}

/**
 * The 200-STATUS BODY-error arm: the upstream answered and its own payload carries the failure. The raw error array
 * rides `data` because that array IS the diagnosis, and summarizing it here is a summary the reader must work back
 * from.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): one of the four upstream-transport envelopes over
 * {@link buildAdapterError}, sharing that cohort's home rationale -- both `tools/` adapters bind it and a shared
 * `tools/` home is foreclosed by the cross-tool ban.
 */
export function upstreamQueryAdapterError(label: string, detail: string, errors: unknown): AdapterErrorResponse {
	return buildAdapterError(`${label} query error: ${detail}`, HTTP_STATUS.BAD_GATEWAY, { errors });
}
